import { Injectable, inject } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Field, SelectObjectOptions, SelectOption } from '@core/models'
import { whitespaceValidationPattern } from '@core/global-util'
import { FieldValidationService } from '@services/field-validation.service'
import { reduce } from 'lodash-es'
import { map } from 'rxjs'

export interface SelectGroupControl {
    [key: string]: FormGroup<OptionControl>
}

export interface OptionControl {
    label: FormControl<string>
    color: FormControl<string>
    icon: FormControl<string>
}

@Injectable()
export class SelectSettingsFormService {
    private readonly fieldValidationService = inject(FieldValidationService)

    private selectGroupControl!: FormGroup

    addGroupControl(option: SelectOption, key: string, field: Field) {
        this.selectGroupControl.setControl(key, this.generateOptionGroup(option, field))
    }

    deleteGroupControl(key: string) {
        this.selectGroupControl.removeControl(key)
    }

    getValueChanges() {
        return this.selectGroupControl.valueChanges.pipe(
            map((value) => {
                return reduce(
                    value,
                    (acc, option, key) => {
                        const updatedOption = {
                            guid: key,
                            label: option.label,
                            color: option.color,
                            icon: option.icon !== 'add_reaction' ? value[key].icon : '',
                        }
                        acc.push(updatedOption)
                        return acc
                    },
                    [] as SelectOption[],
                )
            }),
        )
    }

    getStatusChanges() {
        return this.selectGroupControl.statusChanges
    }

    getOptionGroup(key: string): FormGroup<OptionControl> {
        return this.selectGroupControl?.controls[key] as FormGroup<OptionControl>
    }

    setSelectGroupControl(selectOptions: SelectObjectOptions, field: Field) {
        this.selectGroupControl = new FormGroup<SelectGroupControl>({})
        this.generateSelectGroup(selectOptions, field)
    }

    private generateSelectGroup(selectOptions: SelectObjectOptions, field: Field) {
        return reduce(
            selectOptions,
            (group, option, key) => {
                group.setControl(key, this.generateOptionGroup(option, field))

                return group
            },
            this.selectGroupControl,
        )
    }

    private generateOptionGroup(option: SelectOption, field: Field): FormGroup<OptionControl> {
        const builder = new FormBuilder()

        const labelControl = new FormControl(option.label ?? '', {
            nonNullable: true,
            updateOn: 'blur',
            validators: [Validators.required, Validators.pattern(whitespaceValidationPattern)],
            asyncValidators: this.fieldValidationService.generateFieldValidators(field),
        })

        return builder.nonNullable.group({
            label: labelControl,
            icon: option.icon ?? '',
            color: option.color ?? '',
        })
    }
}
